import './index.less';

import { Button, Col, Collapse, message, Modal, Row, Skeleton, Space, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ApplicationDetailsTab from '../ApplicationDetailsTab';
import InfoGrid from '../InfoGrid';
import InfoList from '../InfoList';
import InfoListRow from '../InfoList/InfoListRow';
import InternalRemarkTable from '../InternalRemarkTable';
import LoadingParagraph from '../LoadingParagraphLong';
import UpdateNetIncomeModal from '../UpdateNetIncomeModal';
import UploadedDocument from '../UploadedDocument';
import { FileTypeEnum } from '../../enum/fileType';
import { numberWithCommas } from '../../helpers/number';
import applicationService from '../../services/applicationService/applicationService';
import agreementService from '../../services/agreementService/agreementService';
import fileService from '../../services/fileService/fileService';
import ekycService from '../../services/ekycService/ekycService';
import groupBy from 'lodash.groupby';
import {
  onCalculateEstimatedMonthlyAmount,
  onCalculateFinalInstalmentAmount,
  onCalculateStampingDutyFee,
  onCalculateTotalInstalmentAmount,
  onCalculateTotalInterest,
} from '../../utils/calculation.util';
import { displayDate } from '../../utils/datetime.util';
import { displayApplicationStatus } from '../../utils/string.util';
import { ApplicationStatusEnum } from '../../enum/applicationStepStatus';
import FinoryOcrTaskRef from '../FinoryOcrTaskRef';
import { beSetupAxiosInstance } from '../../axios/beSetupAxios';

const { Panel } = Collapse;

dayjs.extend(duration);

interface IslamicApplicationDetailsModalProps {
  applicationId: string;
  visible: boolean;
  onClose: () => void;
}

interface LocalApplicationState {
  applicationDetails: any;
  creditRecords: any;
  applicationFiles: any;
  agreementDetails: any;
  ekycFiles: any;
  historyLogs: any;
  internalRemarks: any;
  loading: boolean;
  error: string | null;
}

const IslamicApplicationDetailsModal: React.FC<IslamicApplicationDetailsModalProps> = ({
  applicationId,
  visible,
  onClose,
}) => {
  const navigate = useNavigate();
  const [finoryOcrUrl, setFinoryOcrUrl] = useState('');

  const [state, setState] = useState<LocalApplicationState>({
    applicationDetails: null,
    creditRecords: null,
    applicationFiles: null,
    agreementDetails: null,
    ekycFiles: null,
    historyLogs: null,
    internalRemarks: null,
    loading: false,
    error: null,
  });

  const updateState = (updates: Partial<LocalApplicationState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const fetchApplicationDetails = async (id: string) => {
    updateState({ loading: true, error: null });

    try {
      const applicationDetailsRes = await applicationService.getApplicationDetails({
        applicationId: id,
      });
      updateState({ applicationDetails: applicationDetailsRes });

      // Fetch agreement details if exists
      if (applicationDetailsRes?.agreementId) {
        const agreementDetailsRes = await agreementService.getAgreementDetails({
          agreementId: applicationDetailsRes.agreementId,
        });
        updateState({ agreementDetails: agreementDetailsRes });
      }

      // Fetch other data in parallel
      const [creditRes, filesRes] = await Promise.all([
        applicationService.getApplicationCreditRecord({ applicationId: id }),
        fileService.getFilesByEntityId({ entityId: id }),
      ]);

      // Fetch history logs
      try {
        let historyLogsRes;
        if (applicationDetailsRes?.agreementId) {
          historyLogsRes = await applicationService.getHistoryLogsByApplicationIDAndAgreementID({
            applicationId: id,
            agreementId: applicationDetailsRes.agreementId,
          });
        } else {
          historyLogsRes = await applicationService.getHistoryLogsByApplicationID({
            applicationId: id,
          });
        }
        updateState({ historyLogs: { data: historyLogsRes } });
      } catch (error) {
        console.error('Error fetching history logs:', error);
      }

      // Fetch internal remarks
      try {
        const internalRemarksRes = await applicationService.getInternalRemarksByApplicationID({
          filters: { applicationId: id },
        });
        updateState({ internalRemarks: { data: internalRemarksRes } });
      } catch (error) {
        console.error('Error fetching internal remarks:', error);
      }

      // Group files by type like Redux does
      const groupedFiles = groupBy(filesRes, (file: any) => file.fileType.name.toLowerCase());

      updateState({
        creditRecords: creditRes,
        applicationFiles: { data: groupedFiles },
        loading: false,
      });

      // Fetch ekyc files if available
      try {
        const wiseAiResult = await ekycService.getWiseAiResultByApplicationId({
          applicationId: id,
        });
        if (wiseAiResult?.length > 0 && wiseAiResult[0] != null) {
          const ekycFilesRes = await fileService.getEkycFilesByEntityId({
            entityId: wiseAiResult[0].id,
          });
          console.log('Modal ekyc files result:', ekycFilesRes);
          updateState({ ekycFiles: { data: ekycFilesRes, isLoading: false } });
        }
      } catch (error) {
        console.error('Error fetching ekyc files:', error);
      }
    } catch (error) {
      console.error('Error fetching application details:', error);
      updateState({
        loading: false,
        error: 'Failed to load application details',
      });
    }
  };

  useEffect(() => {
    if (visible && applicationId) {
      fetchApplicationDetails(applicationId);
    }
  }, [visible, applicationId]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!visible) {
      setState({
        applicationDetails: null,
        creditRecords: null,
        applicationFiles: null,
        agreementDetails: null,
        ekycFiles: null,
        historyLogs: null,
        internalRemarks: null,
        loading: false,
        error: null,
      });
      setFinoryOcrUrl('');
    }
  }, [visible]);

  const {
    applicationDetails,
    creditRecords,
    applicationFiles,
    ekycFiles,
    historyLogs,
    internalRemarks,
    loading,
    error,
  } = state;

  // Calculate employment duration
  const employmentStartingFrom = dayjs(applicationDetails?.employmentStartingFrom);
  const currentDate = dayjs();
  const durationCalc = dayjs.duration(currentDate.diff(employmentStartingFrom));
  const years = durationCalc.years();
  const months = durationCalc.months();

  let currentEmploymentDuration = '';
  if (years === 0) {
    currentEmploymentDuration = `${months} ${months <= 1 ? 'month' : 'months'}`;
  } else if (years === 1 && months === 0) {
    currentEmploymentDuration = '1 year';
  } else if (years === 1 && months > 0) {
    currentEmploymentDuration = `1 year ${months} ${months <= 1 ? 'month' : 'months'}`;
  } else if (years > 1 && months === 0) {
    currentEmploymentDuration = `${years} ${years <= 1 ? 'year' : 'years'}`;
  } else {
    currentEmploymentDuration = `${years} ${years === 1 ? 'year' : 'years'} ${months} ${
      months <= 1 ? 'month' : 'months'
    }`;
  }

  const showMiaInCcrisResult = (
    miaTwoTimes: string | undefined,
    miaThreeTimes: string | undefined
  ) => {
    if (
      miaThreeTimes === undefined ||
      miaTwoTimes === undefined ||
      miaThreeTimes === null ||
      miaTwoTimes === null ||
      miaThreeTimes === '' ||
      miaTwoTimes === '' ||
      !miaThreeTimes ||
      !miaTwoTimes
    ) {
      return 'NOT_AVAILABLE';
    }

    if (miaTwoTimes === 'APPROVE') {
      if (miaThreeTimes === 'APPROVE') {
        return 'APPROVE';
      } else {
        return 'REJECT';
      }
    } else if (miaTwoTimes === 'NOT_AVAILABLE') {
      if (miaThreeTimes === 'NOT_AVAILABLE') {
        return 'NOT_AVAILABLE';
      }
    } else {
      return 'REJECT';
    }
  };

  const showCcrisLegalStatus = () => {
    if (creditRecords?.ccris_legal) {
      if (
        creditRecords?.ccris_legal === 'NOT_AVAILABLE' ||
        creditRecords?.experian_legal === 'NOT_AVAILABLE'
      ) {
        return 'NOT_AVAILABLE';
      }

      if (creditRecords?.ccris_legal === 'APPROVE' && creditRecords?.experian_legal === 'APPROVE') {
        return 'APPROVE';
      } else {
        return 'REJECT';
      }
    }
    return '-';
  };

  const loanApplicationData = [
    {
      title: 'Application Date',
      value: displayDate(applicationDetails?.submissionDate),
    },
    {
      title: 'Financing Type',
      value: applicationDetails?.applicationType,
    },
    {
      title: 'Application No.',
      value: applicationDetails?.applicationNo ?? '-',
    },
    {
      title: 'Campaign Code',
      value: applicationDetails?.productReferralCode ?? '-',
    },
    {
      title: 'Applicant Name',
      value: applicationDetails?.applicantFirstName,
    },
    {
      title: 'Applicant NRIC',
      value: applicationDetails?.applicantNric,
    },
    {
      title: 'Contact No.',
      value: applicationDetails?.applicantPhoneNo,
    },
    {
      title: 'Email Address',
      value: applicationDetails?.applicantEmail,
    },
    {
      title: 'Applied Loan Amount',
      value: `RM ${numberWithCommas(applicationDetails?.loanApplied)}`,
    },
    {
      title: 'Monthly Instalment',
      value: `RM ${numberWithCommas(
        onCalculateEstimatedMonthlyAmount(
          applicationDetails?.loanApplied ?? 0,
          applicationDetails?.loanInterestRate ?? 0,
          applicationDetails?.loanTenure ?? 0
        )
      )}`,
    },
    {
      title: 'Interest Rate',
      value: `${applicationDetails?.loanInterestRate} %`,
    },
    {
      title: 'Instalment Period',
      value: applicationDetails?.loanTenure ?? '-',
    },
    {
      title: 'Bank Name',
      value: applicationDetails?.applicantBankName ?? '-',
    },
    {
      title: 'Bank Account No.',
      value: applicationDetails?.applicantPersonalBankNo ?? '-',
    },
    {
      title: 'Security Type',
      value: applicationDetails?.loanType ?? '-',
    },
    {
      title: 'Reason For Loan',
      value: `${applicationDetails?.applicantLoanReason ?? applicationDetails?.loanReason?.name} ${
        applicationDetails?.customReason ? '- ' + applicationDetails?.customReason : ''
      }`,
    },
    {
      title: 'Marketing Consent',
      value: applicationDetails?.applicantAcceptMarketing ? 'YES' : 'NO',
    },
    {
      title: 'Partner ID No',
      value: applicationDetails?.applicantPartnerId ?? '-',
    },
    {
      title: 'Customer Type',
      value: applicationDetails?.customerType,
    },
    {
      title: 'redONE Subscription Date',
      value: displayDate(applicationDetails?.user?.redCashEligibleDate),
    },
    {
      title: 'TIN',
      value: applicationDetails?.user?.tinNumber ?? '-',
    },
    {
      title: 'Discount Amount',
      value: applicationDetails?.productProcessingFeeToDiscount
        ? `RM ${numberWithCommas(applicationDetails?.productProcessingFeeToDiscount)}`
        : '-',
    },
  ];

  const creditAssessmentInfo1 = [
    {
      title: 'Age',
      value: creditRecords?.age ?? '-',
    },
    {
      title: 'Net Income',
      value: `RM ${numberWithCommas(applicationDetails?.actualMonthlySalary ?? 0)}`,
    },
    {
      title: 'Bankruptcy Check (NRVB)',
      value: creditRecords?.bankruptcy_check ?? '-',
    },
    {
      title: 'AMLA (CCDI Report)',
      value: creditRecords?.amla ?? '-',
    },
    {
      title: 'i-Score',
      value: `${creditRecords?.iscore ?? '-'} (${creditRecords?.iscore_value ?? '-'})`,
    },
    {
      title: 'Special Attention Flag',
      value: creditRecords?.special_attention_flag ?? '-',
    },
    {
      title: 'CCRIS Status Code (AKPK, etc.)',
      value: creditRecords?.ccris_status_code ?? '-',
    },
    {
      title: 'CCRIS Legal',
      value: showCcrisLegalStatus(),
    },
  ];

  const creditAssessmentInfo2 = [
    {
      title: 'MIA In CCRIS',
      value: showMiaInCcrisResult(
        creditRecords?.mia_in_ccris_two_times,
        creditRecords?.mia_in_ccris_three_times
      ),
    },
    {
      title: 'Trade Reference',
      value: creditRecords?.trade_reference_amount ?? '-',
    },
    {
      title: 'Years of Current Employment',
      value: applicationDetails?.currentEmploymentDuration ?? '-',
    },
    {
      title: 'Employment Type',
      value: applicationDetails?.applicantEmploymentType,
    },
    {
      title: 'Employment Status',
      value: 'ACTIVE',
    },
    {
      title: 'Risk Profiling',
      value: creditRecords?.risk_profile ?? '-',
    },
    {
      title: 'DSR',
      value: creditRecords?.dsr_value ?? '-',
    },
    {
      title: 'Eligible Loan Amount (ELA)',
      value:
        applicationDetails?.eligibleLoanAmount != null
          ? `RM ${numberWithCommas(applicationDetails?.eligibleLoanAmount)}`
          : '-',
    },
  ];

  const productStampingFee = applicationDetails?.productStampingFee;
  const stampDuty =
    productStampingFee === null || productStampingFee === undefined || productStampingFee === 0
      ? onCalculateStampingDutyFee(
          applicationDetails?.financeAmount ?? 0,
          applicationDetails?.productStampingDutyPercentage ?? 0
        )
      : productStampingFee;

  const applicationSummaryInfo = [
    {
      title: 'Finance Amount',
      value: `RM ${numberWithCommas(applicationDetails?.financeAmount ?? 0)}`,
    },
    {
      title: 'Interest Amount',
      value: `RM ${numberWithCommas(
        onCalculateTotalInterest(
          applicationDetails?.financeAmount ?? 0,
          applicationDetails?.loanInterestRate ?? 0,
          applicationDetails?.loanTenure ?? 0
        )
      )}`,
    },
    {
      title: 'Interest Rate',
      value: applicationDetails?.loanInterestRate
        ? `${applicationDetails?.loanInterestRate} %`
        : '-',
    },
    {
      title: 'Instalment Period',
      value: `${applicationDetails?.loanTenure} months`,
    },
    {
      title: 'Total Instalment Amount',
      value: `RM ${numberWithCommas(
        onCalculateTotalInstalmentAmount(
          applicationDetails?.financeAmount ?? 0,
          applicationDetails?.loanInterestRate ?? 0,
          applicationDetails?.loanTenure ?? 0
        )
      )}`,
    },
    {
      title: 'Monthly Instalment',
      value: `RM ${numberWithCommas(
        onCalculateEstimatedMonthlyAmount(
          applicationDetails?.financeAmount ?? applicationDetails?.financeAmount ?? 0,
          applicationDetails?.loanInterestRate ?? 0,
          applicationDetails?.loanTenure ?? 0
        )
      )}`,
    },
    {
      title: 'Final Instalment Amount',
      value: `RM ${numberWithCommas(
        onCalculateFinalInstalmentAmount(
          applicationDetails?.financeAmount ?? 0,
          applicationDetails?.loanInterestRate ?? 0,
          applicationDetails?.loanTenure ?? 0
        )
      )}`,
    },
    {
      title: 'Net Salary/Income',
      value: `RM ${numberWithCommas(applicationDetails?.actualMonthlySalary)}`,
    },
    {
      title: 'Processing Fee',
      value: `RM ${numberWithCommas(applicationDetails?.productProcessingFee ?? 0)}`,
    },
    {
      title: 'Wakalah Fee',
      value: `RM ${numberWithCommas(applicationDetails?.productWakalahFee ?? 0)}`,
    },
    {
      title: 'Stamp Duty Fee',
      value: stampDuty ? `RM ${numberWithCommas(stampDuty)}` : '-',
    },
    {
      title: 'DSR',
      value: applicationDetails?.dsr ?? '-',
    },
  ];

  const fetchFileList = () => {
    if (applicationDetails?.id) {
      fileService.getFilesByEntityId({ entityId: applicationDetails.id }).then((res) => {
        const groupedFiles = groupBy(res, (file: any) => file.fileType.name.toLowerCase());
        updateState({ applicationFiles: { data: groupedFiles } });
      });
    }
  };

  return (
    <Modal
      title={
        <Space align="center">
          <h3 className="m-0">Application Details</h3>
          {applicationDetails?.applicationStatus && (
            <Tag>{displayApplicationStatus(applicationDetails.applicationStatus)}</Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1400}
      footer={null}
      className="application-details-modal"
    >
      {loading ? (
        <LoadingParagraph />
      ) : error ? (
        <div>Error: {error}</div>
      ) : applicationDetails ? (
        <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <Collapse
            defaultActiveKey={['loanApplication', 'applicationSummary']}
            ghost
            expandIconPosition="end"
          >
            <Panel
              key="loanApplication"
              className="application-details-section-container"
              header={<h3 className="application-details-section-title">Loan Application</h3>}
            >
              <InfoGrid data={loanApplicationData} />
            </Panel>

            <Panel
              key="documents"
              className="application-details-section-container"
              header={<h3 className="application-details-section-title">Documents</h3>}
            >
              <UploadedDocument
                readOnly={true}
                entityId={applicationDetails.id}
                fileType={FileTypeEnum.SALARYSLIP}
                description="Latest 3 months of salary slips (for Salaried Employee) OR Latest Form B/BE with official tax receipt (for Self-Employed)"
                defaultFileList={applicationFiles?.data?.salaryslip}
                callback={fetchFileList}
              />
              <UploadedDocument
                readOnly={true}
                entityId={applicationDetails.id}
                fileType={FileTypeEnum.BANKSTATEMENT}
                description="Latest 3 months of bank statement (showing monthly salary credited or other credited income)"
                defaultFileList={applicationFiles?.data?.bankstatement}
              />
              <UploadedDocument
                readOnly={true}
                entityId={applicationDetails.id}
                fileType={FileTypeEnum.OTHERSRELEVANTDOCUMENTS}
                description="Latest EPF Statement OR Form EA OR Business registration certificate OR other relevant income documents"
                defaultFileList={applicationFiles?.data?.othersrelevantdocuments}
                callback={fetchFileList}
              />
            </Panel>

            {creditRecords != null &&
            (Object.keys(creditRecords) as Array<keyof typeof creditRecords>).some(
              (key) => creditRecords[key] === '-'
            ) ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <Panel
                key="creditAssessment"
                className="application-details-section-container"
                header={<h3 className="application-details-section-title">Credit Assessment</h3>}
              >
                <Row gutter={[16, 16]} className="mb-4">
                  <Col span={12}>
                    <div className="info-container">
                      <InfoListRow title="Bucket" value={applicationDetails?.bucket} />
                    </div>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col sm={{ span: 24 }} md={{ span: 12 }}>
                    <InfoList data={creditAssessmentInfo1} />
                  </Col>
                  <Col sm={{ span: 24 }} md={{ span: 12 }}>
                    <InfoList data={creditAssessmentInfo2} />
                  </Col>
                </Row>
                <br />
                {applicationDetails.applicationStatus !==
                  ApplicationStatusEnum.SUBMITTED_FOR_APPROVAL &&
                  applicationDetails.applicationStatus !==
                    ApplicationStatusEnum.SUBMITTED_FOR_APPROVAL_FROM_PENDING_DISBURSEMENT &&
                  applicationDetails.applicationStatus !== ApplicationStatusEnum.APPROVED && (
                    <Space direction={'horizontal'}>
                      <Button
                        onClick={async () => {
                          const url = `/redCash/admin/api/v1.0/application/${applicationId}/new-engine/isNewApplication/${true}`;
                          const res = await beSetupAxiosInstance.get(url);
                          message.success(
                            'Rerun credit evaluation engine successfully, please refresh the page after 10 second'
                          );
                        }}
                      >
                        Run New Engine
                      </Button>
                    </Space>
                  )}
              </Panel>
            )}

            <Panel
              key="applicationSummary"
              className="application-details-section-container"
              header={<h3 className="application-details-section-title">Application Summary</h3>}
            >
              <InfoGrid data={applicationSummaryInfo} />
              <div className="h-8" />
            </Panel>

            <Panel
              key="internalRemark"
              className="application-details-section-container"
              header={<h3 className="application-details-section-title">Internal Remark</h3>}
            >
              <InternalRemarkTable
                applicationData={applicationDetails}
                internalRemarksData={internalRemarks}
              />
            </Panel>

            <Panel
              key="moreDetails"
              className="application-details-section-container"
              header={<h3 className="application-details-section-title">More Details</h3>}
            >
              <ApplicationDetailsTab
                applicationData={applicationDetails}
                ekycFilesData={ekycFiles}
                historyLogsData={historyLogs}
                readOnlyMode={true}
              />
            </Panel>
          </Collapse>
        </div>
      ) : (
        <div>Application not found</div>
      )}
    </Modal>
  );
};

export default IslamicApplicationDetailsModal;
